import React, { useState, useEffect, useCallback } from 'react';
import { Upload, FileSpreadsheet, TrendingUp, TrendingDown, DollarSign, Loader2, Calendar, PieChart, BarChart3, Download, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPENSE_CATEGORIES = [
    { value: 'advertising', label: 'Advertising' },
    { value: 'bank_fees', label: 'Bank Fees' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'office_supplies', label: 'Office Supplies' },
    { value: 'professional_fees', label: 'Professional Fees' },
    { value: 'rent', label: 'Rent' },
    { value: 'repairs', label: 'Repairs & Maintenance' },
    { value: 'software', label: 'Software' },
    { value: 'subscriptions', label: 'Subscriptions' },
    { value: 'travel', label: 'Travel' },
    { value: 'utilities', label: 'Utilities' },
    { value: 'wages', label: 'Wages & Salaries' },
    { value: 'vehicle', label: 'Vehicle' },
    { value: 'meals', label: 'Meals & Entertainment' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'inventory', label: 'Inventory' },
    { value: 'other_expense', label: 'Other Expense' },
];

const INCOME_CATEGORIES = [
    { value: 'sales', label: 'Sales Revenue' },
    { value: 'service_income', label: 'Service Income' },
    { value: 'interest', label: 'Interest Income' },
    { value: 'refunds', label: 'Refunds' },
    { value: 'other_income', label: 'Other Income' },
];

export const BankStatementUpload = ({ onUploadSuccess }) => {
    const [uploading, setUploading] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadResult, setUploadResult] = useState(null);

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.name.toLowerCase().endsWith('.csv')) {
                toast.error('Please upload a CSV file');
                return;
            }
            setSelectedFile(file);
            setShowDialog(true);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        setUploading(true);
        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const token = localStorage.getItem('finmar_token');
            const response = await fetch(`${API}/bank-statements/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (response.ok) {
                const data = await response.json();
                setUploadResult(data.summary);
                toast.success('Bank statement processed successfully');
                if (onUploadSuccess) onUploadSuccess();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Upload failed');
            }
        } catch (error) {
            toast.error('Failed to upload statement');
        } finally {
            setUploading(false);
        }
    };

    const resetDialog = () => {
        setShowDialog(false);
        setSelectedFile(null);
        setUploadResult(null);
    };

    return (
        <>
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-finmar-gold transition-colors">
                <input
                    type="file"
                    id="bank-statement-upload"
                    className="hidden"
                    onChange={handleFileSelect}
                    accept=".csv"
                    data-testid="bank-statement-input"
                />
                <label htmlFor="bank-statement-upload" className="cursor-pointer">
                    <FileSpreadsheet className="w-10 h-10 mx-auto text-slate-400 mb-3" />
                    <p className="text-sm font-medium text-finmar-navy">Upload Bank Statement (CSV)</p>
                    <p className="text-xs text-slate-500 mt-1">Export from your bank as CSV format</p>
                </label>
            </div>

            <Dialog open={showDialog} onOpenChange={resetDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileSpreadsheet className="w-5 h-5 text-finmar-gold" />
                            {uploadResult ? 'Upload Complete' : 'Upload Bank Statement'}
                        </DialogTitle>
                        <DialogDescription>
                            {uploadResult ? 'Your transactions have been processed' : 'Process your bank statement to track income and expenses'}
                        </DialogDescription>
                    </DialogHeader>

                    {!uploadResult ? (
                        <div className="space-y-4 py-4">
                            {selectedFile && (
                                <div className="bg-slate-50 p-3 rounded-lg flex items-center gap-3">
                                    <FileSpreadsheet className="w-8 h-8 text-finmar-navy" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-finmar-navy truncate">{selectedFile.name}</p>
                                        <p className="text-xs text-slate-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                                    </div>
                                </div>
                            )}
                            <div className="text-sm text-slate-600 bg-blue-50 p-3 rounded-lg">
                                <p className="font-medium text-blue-700 mb-1">Supported Banks:</p>
                                <p>ANZ, CBA, Westpac, NAB, and most Australian banks</p>
                                <p className="mt-2 text-xs">Export your statement as CSV from internet banking</p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4 py-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-green-50 p-4 rounded-lg text-center">
                                    <TrendingUp className="w-6 h-6 text-green-600 mx-auto mb-2" />
                                    <p className="text-xl font-bold text-green-700">${uploadResult.total_income.toLocaleString()}</p>
                                    <p className="text-xs text-green-600">Total Income</p>
                                </div>
                                <div className="bg-red-50 p-4 rounded-lg text-center">
                                    <TrendingDown className="w-6 h-6 text-red-600 mx-auto mb-2" />
                                    <p className="text-xl font-bold text-red-700">${uploadResult.total_expense.toLocaleString()}</p>
                                    <p className="text-xs text-red-600">Total Expenses</p>
                                </div>
                            </div>
                            <div className={`p-4 rounded-lg text-center ${uploadResult.net_position >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                                <p className="text-sm text-slate-600">Net Position</p>
                                <p className={`text-2xl font-bold ${uploadResult.net_position >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                    ${uploadResult.net_position.toLocaleString()}
                                </p>
                            </div>
                            <p className="text-center text-sm text-slate-500">
                                {uploadResult.transaction_count} transactions processed
                            </p>
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={resetDialog}>
                            {uploadResult ? 'Close' : 'Cancel'}
                        </Button>
                        {!uploadResult && (
                            <Button
                                onClick={handleUpload}
                                disabled={uploading}
                                className="bg-finmar-gold hover:bg-finmar-gold/90 text-finmar-navy"
                            >
                                {uploading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4 mr-2" />
                                        Process Statement
                                    </>
                                )}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export const FinancialDashboard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('all');
    const [refreshKey, setRefreshKey] = useState(0);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('finmar_token');
            const response = await fetch(`${API}/financial-dashboard?period=${period}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const result = await response.json();
                setData(result);
            }
        } catch (error) {
            console.error('Failed to fetch financial data:', error);
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => {
        fetchData();
    }, [fetchData, refreshKey]);

    const refresh = () => setRefreshKey(prev => prev + 1);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-finmar-gold" />
            </div>
        );
    }

    if (!data || data.summary.transaction_count === 0) {
        return (
            <div className="text-center py-8 bg-slate-50 rounded-lg">
                <BarChart3 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500 mb-4">No financial data yet</p>
                <p className="text-sm text-slate-400">Upload a bank statement to see your income & expense dashboard</p>
            </div>
        );
    }

    const maxExpense = Math.max(...Object.values(data.expense_by_category || {}), 1);

    return (
        <div className="space-y-6">
            {/* Period Filter */}
            <div className="flex items-center justify-between">
                <h3 className="font-semibold text-finmar-navy flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-finmar-gold" />
                    Financial Overview
                </h3>
                <div className="flex items-center gap-2">
                    <Select value={period} onValueChange={setPeriod}>
                        <SelectTrigger className="w-32">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Time</SelectItem>
                            <SelectItem value="month">Last Month</SelectItem>
                            <SelectItem value="quarter">Last Quarter</SelectItem>
                            <SelectItem value="year">Last Year</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" onClick={refresh}>
                        <RefreshCw className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-green-50 p-4 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-green-600 mb-2" />
                    <p className="text-xs text-green-600">Total Income</p>
                    <p className="text-xl font-bold text-green-700">${data.summary.total_income.toLocaleString()}</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                    <TrendingDown className="w-5 h-5 text-red-600 mb-2" />
                    <p className="text-xs text-red-600">Total Expenses</p>
                    <p className="text-xl font-bold text-red-700">${data.summary.total_expense.toLocaleString()}</p>
                </div>
                <div className={`p-4 rounded-lg ${data.summary.net_position >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                    <DollarSign className={`w-5 h-5 mb-2 ${data.summary.net_position >= 0 ? 'text-blue-600' : 'text-orange-600'}`} />
                    <p className={`text-xs ${data.summary.net_position >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Net Position</p>
                    <p className={`text-xl font-bold ${data.summary.net_position >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                        ${data.summary.net_position.toLocaleString()}
                    </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                    <PieChart className="w-5 h-5 text-purple-600 mb-2" />
                    <p className="text-xs text-purple-600">Tax Deductible</p>
                    <p className="text-xl font-bold text-purple-700">${data.summary.tax_deductible.toLocaleString()}</p>
                </div>
            </div>

            {/* Expense Breakdown */}
            {Object.keys(data.expense_by_category || {}).length > 0 && (
                <div className="bg-white border rounded-lg p-4">
                    <h4 className="font-medium text-finmar-navy mb-4">Expense Breakdown</h4>
                    <div className="space-y-3">
                        {Object.entries(data.expense_by_category).slice(0, 8).map(([category, amount]) => (
                            <div key={category} className="flex items-center gap-3">
                                <span className="text-sm text-slate-600 w-32 truncate capitalize">
                                    {category.replace('_', ' ')}
                                </span>
                                <div className="flex-1 bg-slate-100 rounded-full h-3">
                                    <div
                                        className="bg-red-400 h-3 rounded-full"
                                        style={{ width: `${(amount / maxExpense) * 100}%` }}
                                    />
                                </div>
                                <span className="text-sm font-medium text-finmar-navy w-24 text-right">
                                    ${amount.toLocaleString()}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Monthly Chart */}
            {data.monthly_data && data.monthly_data.length > 0 && (
                <div className="bg-white border rounded-lg p-4">
                    <h4 className="font-medium text-finmar-navy mb-4">Monthly Trend</h4>
                    <div className="flex items-end gap-2 h-40">
                        {data.monthly_data.slice(-6).map((month) => {
                            const maxVal = Math.max(...data.monthly_data.map(m => Math.max(m.income, m.expense)), 1);
                            return (
                                <div key={month.month} className="flex-1 flex flex-col items-center gap-1">
                                    <div className="w-full flex gap-1 items-end h-28">
                                        <div
                                            className="flex-1 bg-green-400 rounded-t"
                                            style={{ height: `${(month.income / maxVal) * 100}%` }}
                                            title={`Income: $${month.income.toLocaleString()}`}
                                        />
                                        <div
                                            className="flex-1 bg-red-400 rounded-t"
                                            style={{ height: `${(month.expense / maxVal) * 100}%` }}
                                            title={`Expense: $${month.expense.toLocaleString()}`}
                                        />
                                    </div>
                                    <span className="text-xs text-slate-500">{month.month.slice(5)}</span>
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex items-center justify-center gap-6 mt-4">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-green-400 rounded" />
                            <span className="text-xs text-slate-500">Income</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-red-400 rounded" />
                            <span className="text-xs text-slate-500">Expense</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BankStatementUpload;
